import { useState, useEffect, useRef, useCallback } from "react";
import { Triangle } from "lucide-react";
import { searchTeamUsers } from "../../../../api/teamChat";
import { ENTITY_FULL_NAME } from "../../../../api/config";

/**
 * MentionDropdown — Watches a Tiptap editor for @mentions and shows
 * an autocomplete dropdown above the cursor.
 *
 * Mount alongside TiptapInput inside a relative-positioned container.
 */

const AI_ENTRY = {
  uuid: "ai_assistant",
  display_name: ENTITY_FULL_NAME,
  email: "Always available",
  is_ai: true,
};

const MentionDropdown = ({ editorRef, teamId, onMention }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [coords, setCoords] = useState(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  /* ── Detect @ pattern in editor ── */
  const checkForMention = useCallback(() => {
    const editor = editorRef?.current?.editor;
    if (!editor) return;

    const { state } = editor.view;
    const { from } = state.selection;
    const textBefore = state.doc.textBetween(
      Math.max(0, from - 50),
      from,
      "\n",
    );

    const match = textBefore.match(/(?:^|[\s\n])@(\w*)$/);

    if (match) {
      setQuery(match[1] || "");
      setIsOpen(true);
      setSelectedIndex(0);

      // Get absolute cursor coords for positioning
      try {
        const cursorCoords = editor.view.coordsAtPos(from);
        setCoords(cursorCoords);
      } catch {
        setCoords(null);
      }
    } else {
      setIsOpen(false);
      setQuery("");
      setResults([]);
    }
  }, [editorRef]);

  /* ── Listen to editor updates ── */
  useEffect(() => {
    const editor = editorRef?.current?.editor;
    if (!editor) return;

    const handleUpdate = () => checkForMention();
    const handleSelectionUpdate = () => checkForMention();

    editor.on("update", handleUpdate);
    editor.on("selectionUpdate", handleSelectionUpdate);

    return () => {
      editor.off("update", handleUpdate);
      editor.off("selectionUpdate", handleSelectionUpdate);
    };
  }, [editorRef?.current?.editor, checkForMention]);

  /* ── Search users when query changes ── */
  useEffect(() => {
    if (!isOpen) return;

    clearTimeout(debounceRef.current);

    const searchQuery = query.length === 0 ? "a" : query;

    debounceRef.current = setTimeout(
      () => {
        searchTeamUsers(searchQuery)
          .then((res) => {
            setResults((res?.data?.users || []).slice(0, 8));
            setSelectedIndex(0);
          })
          .catch(() => setResults([]));
      },
      query.length === 0 ? 100 : 150,
    );

    return () => clearTimeout(debounceRef.current);
  }, [query, isOpen, teamId]);

  /* ── Keyboard navigation ── */
  useEffect(() => {
    if (!isOpen) return;

    const editor = editorRef?.current?.editor;
    if (!editor) return;

    // Total items = search results + AI entry
    const totalItems = results.length + 1;

    const handleKeyDown = (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((prev) => Math.min(prev + 1, totalItems - 1));
        return true;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        return true;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        e.stopPropagation();
        const allItems = [...results, AI_ENTRY];
        selectUser(allItems[selectedIndex]);
        return true;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setIsOpen(false);
        return true;
      }
      return false;
    };

    const dom = editor.view.dom;
    dom.addEventListener("keydown", handleKeyDown, true);
    return () => dom.removeEventListener("keydown", handleKeyDown, true);
  }, [isOpen, results, selectedIndex, editorRef]);

  /* ── Select a user ── */
  const selectUser = useCallback(
    (user) => {
      if (!user) return;

      const editor = editorRef?.current?.editor;
      if (!editor) return;

      const { state } = editor.view;
      const { from } = state.selection;
      const textBefore = state.doc.textBetween(
        Math.max(0, from - 50),
        from,
        "\n",
      );

      const match = textBefore.match(/(?:^|[\s\n])@(\w*)$/);
      if (!match) return;

      const matchLen = match[0].length;
      const startsWithSpace = match[0][0] === " " || match[0][0] === "\n";
      const deleteFrom = from - matchLen + (startsWithSpace ? 1 : 0);

      const displayName = user.display_name || user.first_name || "Unknown";
      const mentionText = `@${displayName} `;

      editor
        .chain()
        .focus()
        .deleteRange({ from: deleteFrom, to: from })
        .insertContent(mentionText)
        .run();

      onMention?.(user.uuid);

      setIsOpen(false);
      setQuery("");
      setResults([]);
    },
    [editorRef, onMention],
  );

  /* ── Click outside to close ── */
  useEffect(() => {
    if (!isOpen) return;

    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  if (!isOpen || !coords) return null;

  // All items: search results + pinned AI entry at bottom
  const allItems = [...results, AI_ENTRY];

  // Position: fixed to viewport, above the cursor line
  const dropdownHeight = Math.min(allItems.length * 40 + 32, 248);

  return (
    <div
      ref={dropdownRef}
      className="fixed z-[100] w-[260px] max-h-[200px] overflow-y-auto bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-xl py-1"
      style={{
        top: coords.top - dropdownHeight - 6,
        left: coords.left,
      }}
    >
      <div className="px-3 py-1.5 border-b border-zinc-100 dark:border-zinc-700">
        <p className="text-[10px] text-zinc-400 dark:text-zinc-500 font-medium uppercase tracking-wide">
          Mention someone
        </p>
      </div>
      {results.map((user, i) => {
        const name = user.display_name || user.first_name || "Unknown";
        const initial = (name[0] || "?").toUpperCase();
        const isSelected = i === selectedIndex;

        return (
          <button
            key={user.uuid}
            onClick={() => selectUser(user)}
            onMouseEnter={() => setSelectedIndex(i)}
            className={[
              "w-full flex items-center gap-x-2.5 px-3 py-1.5 text-left transition-colors",
              isSelected
                ? "bg-sky-50 dark:bg-sky-900/20"
                : "hover:bg-zinc-50 dark:hover:bg-zinc-700/50",
            ].join(" ")}
          >
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center text-[11px] font-medium text-white shrink-0">
              {initial}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-medium text-zinc-700 dark:text-zinc-300 truncate">
                {name}
              </p>
              {user.email && (
                <p className="text-[10px] text-zinc-400 dark:text-zinc-500 truncate">
                  {user.email}
                </p>
              )}
            </div>
          </button>
        );
      })}

      {/* ── Pinned AI entry ── */}
      <div className="border-t border-zinc-100 dark:border-zinc-700 mt-0.5 pt-0.5">
        <button
          onClick={() => selectUser(AI_ENTRY)}
          onMouseEnter={() => setSelectedIndex(results.length)}
          className={[
            "w-full flex items-center gap-x-2.5 px-3 py-1.5 text-left transition-colors",
            selectedIndex === results.length
              ? "bg-sky-50 dark:bg-sky-900/20"
              : "hover:bg-zinc-50 dark:hover:bg-zinc-700/50",
          ].join(" ")}
        >
          <div className="w-6 h-6 rounded-full bg-[#0EA5E9]/10 flex items-center justify-center shrink-0">
            <Triangle className="h-3 w-3" fill="#0EA5E9" stroke="none" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-medium text-[#0EA5E9] dark:text-sky-400 truncate">
              {ENTITY_FULL_NAME}
            </p>
            <p className="text-[10px] text-zinc-400 dark:text-zinc-500 truncate">
              Always available
            </p>
          </div>
          <span className="text-[9px] font-semibold tracking-wide px-1.5 py-0.5 rounded bg-sky-50 dark:bg-sky-900/30 text-[#0369A1] dark:text-sky-400 border border-[#BAE6FD] dark:border-sky-700">
            AI
          </span>
        </button>
      </div>
    </div>
  );
};

export default MentionDropdown;
